import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Form, Button, Modal } from 'react-bootstrap';
import { PageLoader, SectionLoader } from '../../Components/LoadingSpinner';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useBuildingKey } from '../../Helpers/utils';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useUsersData, Userprofile } from '../../Helpers/UsersContext';
import { BaseComponentImpl, RoomImpl, LevelImpl } from '../../types/implementations';
import { translateFieldValue } from '../../Helpers/archiveFieldValues';
import {
    useArchiveHistory,
    formatSwedishDate,
    FIELD_TRANSLATIONS,
    FieldChange,
    HistoryEntry,
    EntityType,
} from '../../hooks/archive/useArchiveHistory';
import { FieldDetailBody } from './HistoryFieldRenderers';
import './BuildingHistoryPage.css';

// ── Presentation helpers ─────────────────────────────────────────

const translateFieldName = (field: string): string => {
    return FIELD_TRANSLATIONS[field] || field;
};

const getUserName = (uid: string | null, users: Userprofile[] | undefined): string => {
    if (!uid) return 'Okänd användare';
    const user = users?.find(u => u.key === uid);
    return user?.name || 'Okänd användare';
};

// ── Component ────────────────────────────────────────────────────

interface FieldDetailModalData {
    change: FieldChange;
    componentName: string;
    archivedAt: string;
}

const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
    components: 'Komponenter',
    levels: 'Våningsplan',
    rooms: 'Rum',
};

const BuildingHistoryPage: React.FC = () => {
    const buildingKey = useBuildingKey();
    const { componentKey, levelKey, roomKey } = useParams<{
        componentKey?: string;
        levelKey?: string;
        roomKey?: string;
    }>();
    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();
    const { users, loadingUsers } = useUsersData();
    const [fieldDetail, setFieldDetail] = useState<FieldDetailModalData | null>(null);
    const [selectedEntityTypes, setSelectedEntityTypes] = useState<Set<EntityType>>(
        new Set(['components', 'levels', 'rooms'])
    );

    const toggleEntityType = (type: EntityType) => {
        setSelectedEntityTypes(prev => {
            const next = new Set(prev);
            if (next.has(type)) {
                // Don't allow deselecting all types
                if (next.size > 1) {
                    next.delete(type);
                }
            } else {
                next.add(type);
            }
            return next;
        });
    };

    // Determine entity mode and get display name
    const entityKey = componentKey || levelKey || roomKey;
    const entityMode: 'component' | 'level' | 'room' | null = componentKey ? 'component' : levelKey ? 'level' : roomKey ? 'room' : null;

    const entityName = useMemo(() => {
        if (!currentBuilding) return null;
        if (componentKey) {
            const comp = currentBuilding.model?.structure?.components?.[componentKey];
            return comp ? new BaseComponentImpl(comp).displayName(currentBuilding) : componentKey;
        }
        if (levelKey) {
            const level = currentBuilding.model?.structure?.levels?.[levelKey];
            return level ? new LevelImpl(level).displayName() : levelKey;
        }
        if (roomKey) {
            const room = currentBuilding.model?.structure?.rooms?.[roomKey];
            return room ? new RoomImpl(room).displayName() : roomKey;
        }
        return null;
    }, [currentBuilding, componentKey, levelKey, roomKey]);

    const buildingName = currentBuilding?.summary?.name || buildingKey || 'Byggnad';

    // Set document title
    useDocumentTitle(
        loadingBuilding ? 'Laddar historik...' :
        buildingError ? 'Fel - Historik' :
        entityName ? `Historik: ${entityName} - ${buildingName}` :
        `Detaljerad historik - ${buildingName}`
    );

    // When viewing a specific entity, use only that type
    // Memoize to prevent infinite render loop in useArchiveHistory
    const entityTypes = useMemo(
        () => {
            if (componentKey) return ['components'] as EntityType[];
            if (levelKey) return ['levels'] as EntityType[];
            if (roomKey) return ['rooms'] as EntityType[];
            return Array.from(selectedEntityTypes);
        },
        [componentKey, levelKey, roomKey, selectedEntityTypes]
    );

    const {
        loading,
        error,
        history,
        groupedHistory,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        clearDateFilter,
        expandedGroups,
        toggleGroup,
        totalChanges,
        filteredChanges,
    } = useArchiveHistory(buildingKey, entityKey, entityTypes);

    useEffect(() => {
        if (buildingKey) {
            fetchBuilding(buildingKey);
        }
    }, [buildingKey, fetchBuilding]);

    // Build breadcrumb items based on entity mode (must be before early returns)
    const breadcrumbItems = useMemo(() => {
        const items: Array<{ text: string; link?: string }> = [
            { text: buildingName, link: `/building/${buildingKey}` }
        ];

        if (entityMode === 'component' && componentKey) {
            items.push({ text: entityName || componentKey, link: `/building/${buildingKey}/component/${componentKey}` });
            items.push({ text: 'Historik' });
        } else if (entityMode === 'level' && levelKey) {
            items.push({ text: entityName || levelKey, link: `/building/${buildingKey}/level/${levelKey}` });
            items.push({ text: 'Historik' });
        } else if (entityMode === 'room' && roomKey) {
            items.push({ text: entityName || roomKey, link: `/building/${buildingKey}/room/${roomKey}` });
            items.push({ text: 'Historik' });
        } else {
            items.push({ text: 'Tidslinje', link: `/building/${buildingKey}/timeline` });
            items.push({ text: 'Detaljerad historik' });
        }

        return items;
    }, [buildingKey, buildingName, entityMode, entityName, componentKey, levelKey, roomKey]);

    if (!buildingKey) return <p>Byggnadsnyckeln saknas</p>;
    if (loadingBuilding) return <PageLoader />;
    if (buildingError) return <Alert variant="danger">{buildingError}</Alert>;
    if (!currentBuilding) return <p>Byggnadsdata saknas</p>;

    const hasDateFilter = startDate !== '' || endDate !== '';

    return (
        <div className="building-history-page">
            <BreadcrumbComponent items={breadcrumbItems} />

            <div className="history-content">
            <div className="building-history-header">
                <h1 className="page-title">{entityName ? `Historik: ${entityName}` : 'Detaljerad historik'}</h1>
                <p className="text-muted">
                    {entityName
                        ? `Ändringar av ${entityName}`
                        : `Ändringar för ${buildingName}`}
                </p>
            </div>

            {/* Entity type filter — only in building view (no specific entity) */}
            {!entityMode && !loading && (
                <div className="history-entity-filter">
                    <span className="history-filter-label">Visa:</span>
                    {(['components', 'levels', 'rooms'] as EntityType[]).map(type => (
                        <Button
                            key={type}
                            variant={selectedEntityTypes.has(type) ? 'primary' : 'outline-secondary'}
                            size="sm"
                            onClick={() => toggleEntityType(type)}
                        >
                            {ENTITY_TYPE_LABELS[type]}
                        </Button>
                    ))}
                </div>
            )}

            {/* Date range filter — only when there is history */}
            {!loading && history.length > 0 && (
                <div className="history-date-filter">
                    <Form.Group className="history-date-input">
                        <Form.Label>Från</Form.Label>
                        <Form.Control
                            type="date"
                            value={startDate}
                            max={endDate || undefined}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="history-date-input">
                        <Form.Label>Till</Form.Label>
                        <Form.Control
                            type="date"
                            value={endDate}
                            min={startDate || undefined}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </Form.Group>
                    {hasDateFilter && (
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="history-clear-btn"
                            onClick={clearDateFilter}
                        >
                            Rensa
                        </Button>
                    )}
                </div>
            )}

            {/* Loading */}
            {loading && (
                <SectionLoader text="Laddar historik..." />
            )}

            {/* Error */}
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Empty state — no history at all */}
            {!loading && !error && history.length === 0 && (
                <div className="text-center py-5">
                    <p className="text-muted">
                        {entityName
                            ? `Inga ändringar har registrerats för ${entityName}`
                            : 'Inga ändringar har registrerats för denna byggnad'}
                    </p>
                </div>
            )}

            {/* Empty state — filter returned nothing */}
            {!loading && !error && history.length > 0 && groupedHistory.length === 0 && (
                <div className="text-center py-5">
                    <p className="text-muted">
                        Inga ändringar hittades för vald period
                    </p>
                </div>
            )}

            {/* Accordion groups */}
            {!loading && !error && groupedHistory.length > 0 && (
                <div className="history-accordion">
                    {groupedHistory.map((group) => {
                        const isExpanded = expandedGroups.has(group.groupKey);
                        const entityCount = new Set(group.entries.map(e => `${e.entityType}-${e.entityKey}`)).size;
                        const changeCount = group.entries.reduce((n, e) => n + e.changes.length, 0);
                        const userLabel = loadingUsers
                            ? '...'
                            : group.userUids.length === 0
                                ? 'Okänd användare'
                                : group.userUids.length === 1
                                    ? getUserName(group.userUids[0], users)
                                    : `${group.userUids.length} användare`;

                        return (
                            <div key={group.groupKey} className="history-group">
                                <div
                                    className="history-group-header"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => toggleGroup(group.groupKey)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleGroup(group.groupKey);
                                        }
                                    }}
                                >
                                    <span className="history-group-chevron">
                                        {isExpanded
                                            ? <ChevronDown size={18} />
                                            : <ChevronRight size={18} />
                                        }
                                    </span>
                                    <span className="history-group-date">
                                        {formatSwedishDate(group.dateKey)}
                                    </span>
                                    <span className="history-group-separator">&mdash;</span>
                                    <span
                                        className="history-group-user"
                                        title={group.userUids.length > 1
                                            ? group.userUids.map(uid => getUserName(uid, users)).join(', ')
                                            : undefined}
                                    >
                                        {userLabel}
                                    </span>
                                    <span className="history-group-separator">&mdash;</span>
                                    <span className="history-group-stats">
                                        {entityCount} {entityCount === 1 ? 'objekt' : 'objekt'},{' '}
                                        {changeCount} {changeCount === 1 ? 'fältändring' : 'fältändringar'}
                                    </span>
                                </div>

                                {isExpanded && (
                                    <div className="history-group-body">
                                        {group.entries.map((entry) => (
                                            <HistoryEntryBlock
                                                key={`${entry.componentKey}-${entry.archiveKey}`}
                                                entry={entry}
                                                onFieldClick={(change) => setFieldDetail({
                                                    change,
                                                    componentName: entry.componentName,
                                                    archivedAt: entry.archivedAt,
                                                })}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Summary counter */}
            {!loading && history.length > 0 && (
                <p className="text-muted mt-3 history-counter">
                    Visar {filteredChanges} av {totalChanges} fältändringar
                </p>
            )}
            </div>

            {/* Field detail modal */}
            <Modal show={fieldDetail !== null} onHide={() => setFieldDetail(null)} centered>
                {fieldDetail && (
                    <>
                        <Modal.Header closeButton>
                            <div>
                                <Modal.Title>{fieldDetail.componentName}</Modal.Title>
                                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                                    {translateFieldName(fieldDetail.change.field)} — {fieldDetail.archivedAt}
                                </p>
                            </div>
                        </Modal.Header>
                        <Modal.Body>
                            <FieldDetailBody
                                change={fieldDetail.change}
                                users={users}
                                rooms={currentBuilding?.model?.structure?.rooms}
                                levels={currentBuilding?.model?.structure?.levels}
                                building={currentBuilding}
                                translateFieldValue={translateFieldValue}
                            />
                        </Modal.Body>
                    </>
                )}
            </Modal>
        </div>
    );
};

// ── Change grouping helpers ───────────────────────────────────────

type ChangeAction = 'added' | 'changed' | 'removed';

function classifyChange(change: FieldChange): ChangeAction {
    const hasOld = change.oldValue != null && change.oldValue !== '';
    const hasNew = change.newValue != null && change.newValue !== '';
    if (!hasOld && hasNew) return 'added';
    if (hasOld && !hasNew) return 'removed';
    return 'changed';
}

const ACTION_LABELS: Record<ChangeAction, string> = {
    added: 'Lade till',
    changed: 'Ändrade',
    removed: 'Tog bort',
};

// ── Sub-component: single entry within a group ───────────────────

interface HistoryEntryBlockProps {
    entry: HistoryEntry;
    onFieldClick: (change: FieldChange) => void;
}

const HistoryEntryBlock: React.FC<HistoryEntryBlockProps> = ({ entry, onFieldClick }) => {
    const time = entry.archivedAt.substring(11, 16); // HH:mm

    // Group changes by action
    const groups: Record<ChangeAction, FieldChange[]> = { added: [], changed: [], removed: [] };
    for (const change of entry.changes) {
        groups[classifyChange(change)].push(change);
    }

    const actionOrder: ChangeAction[] = ['changed', 'added', 'removed'];

    return (
        <div className="history-entry">
            <div className="history-entry-header">
                <span className="history-entry-name">{entry.componentName}</span>
                <span className="history-entry-time">({time})</span>
            </div>
            <div className="history-entry-changes">
                {actionOrder.map((action) => {
                    const changes = groups[action];
                    if (changes.length === 0) return null;
                    return (
                        <span key={action} className="history-action-group">
                            <span className={`history-action history-action--${action}`}>
                                {ACTION_LABELS[action]}
                            </span>{' '}
                            {changes.map((change, i) => (
                                <React.Fragment key={change.field}>
                                    {i > 0 && ', '}
                                    <span
                                        className="history-field-link"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => onFieldClick(change)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onFieldClick(change);
                                            }
                                        }}
                                    >
                                        {translateFieldName(change.field).toLowerCase()}
                                    </span>
                                </React.Fragment>
                            ))}
                            {'. '}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

export default BuildingHistoryPage;
